/**
 * Tattoo fonts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_meaning`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const tattooFontsPack: ToilePack = {
    slug: 'tattoo-fonts',
    category: 'tattoo',
    name: 'Tattoo fonts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'alphabet', label: 'Alphabet', subject: 'the full alphabet A to Z in capitals and lowercase, set in even rows with a margin around the page'},
        {key: 'word', label: 'Sample word', subject: 'the word "Forever" repeated in five rows, each row a different weight or slant of the same lettering style'},
        {key: 'numbers', label: 'Numbers', subject: 'the numbers 0 to 9 and the Roman numerals I to XII in two neat rows, for date tattoos'},
    ],
    styles: [
        {key: 'blackletter', label: 'Blackletter', style: 'a tall blackletter face, sharp angular strokes, thick vertical stems and diamond-shaped serifs', palette: 'black ink only', framing: 'The design centered on the sheet with a wide white margin on every side'},
        {key: 'script', label: 'Script', style: 'a flowing connected script, thin upstrokes, thick downstrokes and looping capitals', palette: 'black ink only', framing: 'The design centered on the sheet with a wide white margin on every side'},
        {key: 'serif', label: 'Block serif', style: 'a clean block serif face, even stems, crisp square serifs and wide letter spacing', palette: 'black ink only', framing: 'The design centered on the sheet with a wide white margin on every side'},
    ],
    directions: ['your name', 'red ink', 'initials'],
    directionHint: 'try: your name',
    seo: {
        title: 'Tattoo fonts: alphabet, sample word, numbers',
        metaDescription: 'Compare tattoo fonts on one sheet: a full alphabet, the word Forever and the numbers 0 to 9 in three lettering styles.',
        h1: 'Tattoo fonts: alphabet, sample word, numbers',
        tagline: 'Three lettering styles, each shown as an alphabet, a word and numbers.',
        intro: 'Before a name or a word is tattooed, people compare lettering styles side by side on one sheet. Each sheet shows a full alphabet, the sample word Forever in five weights or slants, and the numbers 0 to 9 with Roman numerals for dates, in blackletter, script or block serif. Type another word, and the same three styles carry it.',
        useCases: [
            'choosing a lettering style for a name or a word',
            'a reference sheet for the tattoo artist',
            'a wall print of an alphabet',
        ],
        faq: [
            {q: 'What is on each sheet?', a: 'A full alphabet, the word Forever set in five rows of different weight or slant, and the numbers 0 to 9 with Roman numerals.'},
            {q: 'What are the three styles?', a: 'Blackletter is tall with sharp angular strokes, script is a flowing connected hand with looping capitals, and block serif is a clean face with square serifs.'},
            {q: 'Can I type my own word?', a: 'Yes. Type the word you want and the three styles are applied to it. The result is a request, not a promised image.'},
            {q: 'Are these shown on skin?', a: 'No. Each sheet is shown alone on white paper.'},
        ],
    },
}
